import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { showToast } from '../store/toast/toastSlice';
import { selectLocations } from '../store/locations/locationsSlice';
import type { DayKey } from '../constants/weekDayConstants';
import { hasSameDays, withDayToggled } from '../services/openingDaysService';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import { useOpeningDays } from './useOpeningDays';

export interface UseOpeningDaysEditorResult {
  // The days ticked on the page: the unsaved edit, or else the saved days.
  selectedDays: DayKey[];
  isLoading: boolean;
  hasLoadError: boolean;
  // Every day unticked, which the backend refuses.
  hasNoDays: boolean;
  canSave: boolean;
  toggleDay: (day: DayKey) => void;
  retry: () => void;
  // Never rejects: a failure is reported through the error dialog.
  save: () => Promise<void>;
}

interface OpeningDaysDraft {
  locationId: string;
  days: DayKey[];
}

// The opening-days page's unsaved edit and its save. The page stays mounted when
// the location changes, so the edit is tied to the location it was made at and
// dropped on a switch, and a save settling after the switch leaves the next
// location's edit alone, skips the success toast and names its location in a
// failure.
export function useOpeningDaysEditor(locationId: string | null): UseOpeningDaysEditorResult {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const locations = useAppSelector(selectLocations);
  const { openingDays, isLoading, hasLoadError, retry, saveOpeningDays } =
    useOpeningDays(locationId);
  const [draft, setDraft] = useState<OpeningDaysDraft | null>(null);
  if (draft && draft.locationId !== locationId) setDraft(null);

  // Where the user is now, read by a save that settles later.
  const currentLocationIdRef = useRef(locationId);
  useEffect(() => {
    currentLocationIdRef.current = locationId;
  }, [locationId]);

  const selectedDays = draft?.locationId === locationId ? draft.days : openingDays;
  const hasNoDays = !isLoading && !hasLoadError && selectedDays.length === 0;
  const canSave = !isLoading && !hasNoDays && !hasSameDays(selectedDays, openingDays);

  const toggleDay = (day: DayKey): void => {
    if (!locationId) return;
    setDraft({ locationId, days: withDayToggled(selectedDays, day) });
  };

  const save = async (): Promise<void> => {
    const savedLocationId = locationId;
    try {
      await saveOpeningDays(selectedDays);
    } catch (e) {
      console.error(`Failed to save opening days for location ${savedLocationId}:`, e);
      const locationName = locations.find(({ id }) => id === savedLocationId)?.name;
      const fallback = locationName
        ? t('config-opening-days-save-error-for', { location: locationName })
        : t('config-opening-days-save-error');
      dispatch(showError({ message: getApiErrorMessage(e, fallback) }));
      return;
    }
    setDraft((current) => (current?.locationId === savedLocationId ? null : current));
    if (currentLocationIdRef.current === savedLocationId) {
      dispatch(showToast({ message: t('config-opening-days-saved') }));
    }
  };

  return { selectedDays, isLoading, hasLoadError, hasNoDays, canSave, toggleDay, retry, save };
}
